import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
} from "@mui/material";
import { getCurrencies } from "services/currencyService";

interface CurrencyMultiSelectProps {
  label?: string;
  value?: string[];
  onChange: (currencies: string[]) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

const CurrencyMultiSelect = ({
  label = "Select Currencies",
  value = [],
  onChange,
  error,
  helperText,
  disabled = false,
  fullWidth = true,
}: CurrencyMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;

  useEffect(() => {
    let active = true;

    if (!open) {
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const response = await getCurrencies({
          pageNo: 1,
          pageSize,
          searchValue: inputValue || undefined,
        });

        if (active && response?.data?.currencies) {
          const isoCodes: string[] = response.data.currencies
            .map((c: any) => c.isoCode)
            .filter(Boolean);

          // Ensure currently selected values are always present
          const missing = value.filter((v) => !isoCodes.includes(v));
          setOptions([...missing, ...isoCodes]);
          setHasMore(isoCodes.length === pageSize);
          setPageNo(1);
        }
      } catch (err) {
        console.error("Failed to fetch currencies:", err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, inputValue]);

  const loadMore = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const nextPage = pageNo + 1;
      const response = await getCurrencies({
        pageNo: nextPage,
        pageSize,
        searchValue: inputValue || undefined,
      });

      if (response?.data?.currencies) {
        const isoCodes: string[] = response.data.currencies
          .map((c: any) => c.isoCode)
          .filter(Boolean);

        setOptions((prev) => {
          const existing = new Set(prev);
          return [...prev, ...isoCodes.filter((c) => !existing.has(c))];
        });
        setHasMore(isoCodes.length === pageSize);
        setPageNo(nextPage);
      }
    } catch (err) {
      console.error("Failed to fetch more currencies:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleListboxScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const node = event.currentTarget;
    if (
      node.scrollTop + node.clientHeight >= node.scrollHeight - 10 &&
      hasMore &&
      !loading
    ) {
      loadMore();
    }
  };

  return (
    <Autocomplete
      multiple
      fullWidth={fullWidth}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(_event, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
          setPageNo(1);
          setHasMore(true);
        }
      }}
      isOptionEqualToValue={(option, val) => option === val}
      getOptionLabel={(option) => option}
      options={options}
      loading={loading}
      loadingText="Loading currencies..."
      noOptionsText="No currencies found"
      ListboxProps={{
        onScroll: handleListboxScroll,
        style: { maxHeight: "400px" },
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={option}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          error={Boolean(error)}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default CurrencyMultiSelect;
